import React, { useEffect, useState, useMemo } from 'react'

import {
  formatCurrency,
  formatNumber,
  getPaymentOption,
  getAssignmentPaymentMethodKeys,
  getAssignmentPaymentMethodLabel,
  getAssignmentPaymentAmountDescription,
  getAmountDue,
} from '../../../utilities/helpers'

import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Stack from 'react-bootstrap/Stack'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import { LoadingContainer } from '../../../components/LoadingContainer'
import SelectedTickets from '../SelectedTickets'
import { PaymentPlan } from './PaymentPlan'
import { SelectPaymentDueDate } from '../SelectPaymentDueDate'
import { SavedCard } from '../../../components/SavedCard'
import CardForm from '../CardForm'
import { InfoCard } from './InfoCard'
import { DonationCard } from './DonationCard'

import { constants, isCompFlexPack } from '@blocktickets/shared-utils'
import AuthService from '../../../utilities/services/auth.service'
import { SelectedUser } from '../AssignTicketsModalContent/SelectedUser'
const { PAYMENT_TYPE } = constants

function RemoveCustomFeeTrashIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function PricingV2({
  selectedTickets,
  showRemoveSeat,
  onRemoveSeat,
  accessPassQuantity,
  onAccessPassQuantityChange,
  isPackage,
  assignTo,
  existingUser,
  price,
  setPrice,
  priceTiers,
  paymentMethod,
  setPaymentMethod,
  allowEditPrice,
  pricingObject,
  paymentSchedule,
  setPaymentSchedule,
  installmentMonths,
  setInstallmentMonths,
  paymentPlans,
  setPaymentDate,
  amountToPay,
  totalAmount,
  eventPackage,
  dueDateOpt,
  setDueDateOpt,
  customDueDate,
  setCustomDueDate,
  buyerTypes,
  selectedBuyerType,
  setSelectedBuyerType,
  compLabels,
  selectedCompLabel,
  setSelectedCompLabel,
  clientSecret,
  initialClientSecret,
  setCard,
  setStripe,
  setIsCardValid,
  savedCards,
  isLoadingCards,
  selectedCard,
  setSelectedCard,
  openDelete,
  selectedCardOpt,
  setSelectedCardOpt,
  isReserving = false,
  customFeesList = [],
  customFeeRows = [],
  addCustomFeeRow,
  removeCustomFeeRow,
  setCustomFeeRowFeeId,
  totalCustomFeeAmount = 0,
  customFeeSummaryLines = [],
  organizationUUIDForAssignment,
  donationEvaluation = null,
  selectedDonationRuleId,
  setSelectedDonationRuleId,
  configuredDonations = [],
  onApplyDonation,
  donationAmount = 0,
  setDonationAmount,
  donationExternalPending = false,
  setDonationExternalPending,
  onViewDonation,
  donationChargeAmount = 0,
  supplementalFeeOnly = false,
}) {
  const paymentPlansEnabled = Boolean(
    AuthService.getOrg()?.[0]?.setting?.paymentPlans
  )

  const handlePaymentSchedule = (e) => {
    const paymentSchedule = e.target.checked ? 'payment-plan' : 'one-time'
    setPaymentSchedule(paymentSchedule)
  }

  const posTerminalProfile = AuthService.getProfile()
  const hasPosTerminal = Boolean(posTerminalProfile?.location)
  const ALLOWED_KEYS = getAssignmentPaymentMethodKeys(
    organizationUUIDForAssignment,
    posTerminalProfile
  )

  const filteredOptions = Object.fromEntries(
    ALLOWED_KEYS.map((key) => [key, PAYMENT_TYPE[key]])
  )

  const requiresDonation = Boolean(donationEvaluation?.requiresDonation)
  const satisfiedDonationRules = donationEvaluation?.satisfiedRules || []
  const activeDonationRule = useMemo(() => {
    const unmet = donationEvaluation?.unmetRules || []
    if (!unmet.length) return null
    if (unmet.length === 1) return unmet[0]
    return unmet.find(
      (rule) => String(rule.id) === String(selectedDonationRuleId)
    )
  }, [donationEvaluation, selectedDonationRuleId])
  const unmetDonationRules = donationEvaluation?.unmetRules || []
  const isMultiRuleDonation = unmetDonationRules.length > 1
  const showDonationSection =
    satisfiedDonationRules.length > 0 || unmetDonationRules.length > 0

  const resolvedDonationAmount = useMemo(() => {
    if (!requiresDonation) return 0
    if (isMultiRuleDonation) {
      return configuredDonations
        .filter((entry) => !entry.externalPending)
        .reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0)
    }
    return Number(donationAmount) || 0
  }, [
    requiresDonation,
    isMultiRuleDonation,
    configuredDonations,
    donationAmount,
  ])

  const donationLineItems = useMemo(() => {
    if (!requiresDonation) return []
    const ruleNameById = new Map(
      [...unmetDonationRules, ...satisfiedDonationRules].map((rule) => [
        String(rule.id),
        rule.name,
      ])
    )
    configuredDonations.forEach((entry) => {
      if (entry.ruleName) {
        ruleNameById.set(String(entry.ruleId), entry.ruleName)
      }
    })

    if (isMultiRuleDonation) {
      return configuredDonations
        .filter((entry) => !entry.externalPending && Number(entry.amount) > 0)
        .map((entry) => {
          const label =
            entry.ruleName ||
            ruleNameById.get(String(entry.ruleId)) ||
            'Donation'
          return {
            label: `${label} Donation`,
            amount: Number(entry.amount) || 0,
          }
        })
    }
    if (!activeDonationRule || donationExternalPending) return []
    return [
      {
        label: activeDonationRule?.name
          ? `${activeDonationRule.name} Donation`
          : 'Donation',
        amount: resolvedDonationAmount,
      },
    ]
  }, [
    requiresDonation,
    isMultiRuleDonation,
    configuredDonations,
    activeDonationRule,
    donationExternalPending,
    resolvedDonationAmount,
    unmetDonationRules,
    satisfiedDonationRules,
  ])

  const showDonationInSummary = donationChargeAmount > 0
  const cardDonationAmount = showDonationInSummary ? donationChargeAmount : 0

  const sortedPriceTiers = useMemo(() => {
    if (!Array.isArray(priceTiers)) return []
    return [...priceTiers].sort(
      (a, b) => (Number(a?.price) || 0) - (Number(b?.price) || 0)
    )
  }, [priceTiers])

  const supplementalPaymentMethods = new Set(['CARD', 'POS', 'CASH', 'CHECK'])
  const filteredOptionsNoExchange = Object.fromEntries(
    Object.entries(filteredOptions).filter(
      ([key]) =>
        key !== 'EXCHANGE' &&
        (!supplementalFeeOnly || supplementalPaymentMethods.has(key))
    )
  )

  const [priceHolder, setPriceHolder] = useState(price ?? '')
  const hasPricePerTicketProvided =
    price !== undefined && price !== null && String(price).trim() !== ''
  const showZeroServiceFee =
    (Boolean(eventPackage?.flexPackMode) && isCompFlexPack(eventPackage)) ||
    (Boolean(eventPackage?.accessPassMode) &&
      (Number(
        String(price ?? '').trim() !== '' ? price : eventPackage?.price
      ) || 0) === 0)

  // Keep local input state aligned with parent value.
  useEffect(() => {
    if (!isPackage) setPriceHolder(price ?? '')
  }, [price, isPackage])

  // In complete-assignment flow, reserved should not be selectable again.
  useEffect(() => {
    if (isReserving && paymentMethod === 'RESERVED') {
      setPaymentMethod('')
    }
  }, [isReserving, paymentMethod, setPaymentMethod])

  useEffect(() => {
    if (paymentMethod === 'POS' && !hasPosTerminal) {
      setPaymentMethod('')
    }
  }, [paymentMethod, hasPosTerminal, setPaymentMethod])

  useEffect(() => {
    if (
      paymentMethod === 'PAYROLL_DEDUCTION' &&
      !ALLOWED_KEYS.includes('PAYROLL_DEDUCTION')
    ) {
      setPaymentMethod('')
    }
  }, [
    paymentMethod,
    setPaymentMethod,
    ALLOWED_KEYS,
    organizationUUIDForAssignment,
  ])

  useEffect(() => {
    if (!paymentPlansEnabled && paymentSchedule === 'payment-plan') {
      setPaymentSchedule('one-time')
    }
  }, [paymentPlansEnabled, paymentSchedule, setPaymentSchedule])

  return (
    <Stack gap={4}>
      <SelectedUser user={assignTo} />
      {!supplementalFeeOnly && (
        <SelectedTickets
          selected={selectedTickets}
          showRemoveSeat={showRemoveSeat}
          onRemoveSeat={onRemoveSeat}
        />
      )}
      {typeof onAccessPassQuantityChange === 'function' && (
        <Form.Group controlId="access-pass-quantity">
          <Form.Label className="small small-bold">Quantity</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={100}
            value={accessPassQuantity || 1}
            onChange={(e) => onAccessPassQuantityChange(e.target.value)}
            aria-label="Access pass quantity"
          />
          <Form.Text className="text-muted">
            Number of access passes to assign to this purchaser.
          </Form.Text>
        </Form.Group>
      )}
      {allowEditPrice && (
        <Form.Group>
          {isPackage ? (
            <>
              <h5 id="pricing-tier" className="small small-bold">
                Pricing Tier
              </h5>
              <Form.Select
                name="price-tier"
                onChange={(e) => setPrice(e.target.value)}
                value={price || ''}
                required
                disabled={paymentMethod === 'COMP'}
                aria-labelledby="pricing-tier"
                autoFocus
              >
                <option disabled hidden value="">
                  Select a price tier
                </option>
                {sortedPriceTiers.map((key, idx) => (
                  <option key={idx} value={key.price}>
                    {key?.name} ({formatCurrency(key.price)})
                  </option>
                ))}
              </Form.Select>
            </>
          ) : (
            <>
              <Form.Label>Price Per Ticket</Form.Label>
              <InputGroup>
                <InputGroup.Text id="price">$</InputGroup.Text>
                <Form.Control
                  placeholder="Amount"
                  id="price"
                  name="price"
                  pattern="^[0-9.]*$"
                  // we cannot spam stripe with payment intents that are invalid
                  // make sure price is valid - can only enter numbers and decimals
                  onChange={(e) =>
                    setPriceHolder(
                      e.target.value === '' || e.target.validity.valid
                        ? e.target.value
                        : priceHolder
                    )
                  }
                  // only send one payment intent request on blur
                  onBlur={(e) => setPrice(e.target.value)}
                  value={priceHolder}
                  required
                  autoFocus
                  disabled={paymentMethod === 'COMP'}
                />
              </InputGroup>
            </>
          )}
        </Form.Group>
      )}
      {isPackage && !supplementalFeeOnly && showDonationSection && (
        <DonationCard
          unmetRules={donationEvaluation?.unmetRules || []}
          satisfiedRules={satisfiedDonationRules}
          configuredDonations={configuredDonations}
          selectedRuleId={selectedDonationRuleId}
          setSelectedRuleId={setSelectedDonationRuleId}
          donationAmount={donationAmount}
          setDonationAmount={setDonationAmount}
          externalPending={donationExternalPending}
          setExternalPending={setDonationExternalPending}
          onApplyDonation={onApplyDonation}
          ticketsAffected={donationEvaluation?.ticketsAffected}
          onViewDonation={onViewDonation}
        />
      )}
      {organizationUUIDForAssignment &&
        customFeesList.length > 0 &&
        paymentMethod !== 'COMP' &&
        paymentMethod !== 'RESERVED' && (
          <Stack gap={2}>
            <button
              type="button"
              className="btn btn-link p-0 text-start text-decoration-none"
              onClick={addCustomFeeRow}
            >
              + Add Custom Fee
            </button>
            {customFeeRows.map((row) => (
              <Stack
                key={row.id}
                direction="horizontal"
                gap={2}
                className="align-items-start"
              >
                <Form.Select
                  className="flex-grow-1"
                  aria-label="Custom fee"
                  value={row.feeId ?? ''}
                  onChange={(e) =>
                    setCustomFeeRowFeeId(
                      row.id,
                      e.target.value === '' ? null : e.target.value
                    )
                  }
                >
                  <option value="">Select a custom fee</option>
                  {customFeesList
                    .filter((fee) => {
                      const attrs = fee.attributes ?? fee
                      return !supplementalFeeOnly || attrs?.option !== 'percent'
                    })
                    .map((fee) => {
                      const a = fee.attributes ?? fee
                      return (
                        <option key={fee.id} value={fee.id}>
                          {a?.name}{' '}
                          {a?.option === 'percent'
                            ? `(${Number(a?.feeAmount ?? 0)}%)`
                            : `(${formatCurrency(a?.feeAmount ?? 0)})`}
                        </option>
                      )
                    })}
                </Form.Select>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                  onClick={() => removeCustomFeeRow(row.id)}
                  aria-label="Remove custom fee row"
                >
                  <RemoveCustomFeeTrashIcon />
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      <Row>
        <Col>
          <div>
            <h5 id="payment-method" className="small small-bold">
              Payment Type
            </h5>
            <Form.Select
              name="payment-method"
              onChange={(e) => setPaymentMethod(e.target.value)}
              value={paymentMethod || ''}
              required
              aria-labelledby="payment-method"
            >
              <option disabled hidden value="">
                Select payment method
              </option>
              {Object.entries(filteredOptionsNoExchange)
                .sort(([a], [b]) =>
                  getAssignmentPaymentMethodLabel(a).localeCompare(
                    getAssignmentPaymentMethodLabel(b)
                  )
                )
                .map(([option, idx]) => (
                  <option
                    key={idx}
                    value={option}
                    disabled={isReserving && option === 'RESERVED'}
                  >
                    {getAssignmentPaymentMethodLabel(option)}
                  </option>
                ))}
            </Form.Select>
          </div>
        </Col>
      </Row>
      {!supplementalFeeOnly &&
        paymentPlansEnabled &&
        paymentMethod === 'CARD' && (
          <Stack direction="horizontal" gap={3}>
            <Form.Check
              type="switch"
              id="payment-plan"
              name="payment-schedule"
              label="Payment Plan"
              checked={paymentSchedule === 'payment-plan'}
              onChange={handlePaymentSchedule}
            />
          </Stack>
        )}
      {!supplementalFeeOnly &&
        paymentPlansEnabled &&
        paymentSchedule === 'payment-plan' && (
          <>
            <div>
              <h5 id="payment-plan" className="small small-bold">
                Payment Plan
              </h5>
              <Form.Select
                name="payment-plan"
                onChange={(e) => setInstallmentMonths(Number(e.target.value))}
                value={installmentMonths || ''}
                required
                aria-labelledby="payment-plan"
              >
                <option disabled hidden value="">
                  Select a payment plan
                </option>
                {[...Array(11)].map((_, idx) => {
                  const month = idx + 2
                  return (
                    <option key={month} value={month}>
                      {month} Months Payment Plan
                    </option>
                  )
                })}
              </Form.Select>
            </div>
            {paymentPlans && Object.keys(paymentPlans)?.length > 0 && (
              <Stack gap={3}>
                <Stack as="ul" gap={3}>
                  {Object.values(paymentPlans)?.map((plan, idx) => (
                    <PaymentPlan
                      key={idx}
                      plan={plan}
                      idx={idx}
                      setPaymentDate={setPaymentDate}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
          </>
        )}
      {paymentMethod === 'POS' && hasPosTerminal && (
        <Card body className="card--xs info-card card-with-border">
          <Card.Text className="info-text info-text-md">
            Payment will be collected on your assigned terminal
            {posTerminalProfile?.label ? (
              <>
                {' '}
                (
                <span className="text-primary">{posTerminalProfile.label}</span>
                )
              </>
            ) : null}
            .
          </Card.Text>
        </Card>
      )}
      {/* credit-card */}
      {paymentMethod === 'CARD' && existingUser ? (
        <>
          <Stack
            direction="horizontal"
            gap={3}
            className="horizontal-input-group"
          >
            <Form.Check
              type="radio"
              id="saved-card"
              name="card-opt"
              label="Use a saved card"
              checked={selectedCardOpt === 'saved-card'}
              onChange={() => setSelectedCardOpt('saved-card')}
            />
            <Form.Check
              type="radio"
              id="new-card"
              name="card-opt"
              label="Use a new card"
              checked={selectedCardOpt === 'new-card'}
              onChange={() => setSelectedCardOpt('new-card')}
            />
          </Stack>
          {selectedCardOpt === 'saved-card' ? (
            <>
              {isLoadingCards ? (
                <LoadingContainer />
              ) : (
                <Stack gap={3}>
                  <h1 className="normal normal-bold m-0">Saved cards</h1>
                  {savedCards && savedCards?.length > 0 ? (
                    <Stack gap={3} as="ul">
                      {savedCards?.map((card, idx) => (
                        <SavedCard
                          key={idx}
                          card={card}
                          user={existingUser}
                          selected={selectedCard}
                          setSelected={setSelectedCard}
                          selectable={true}
                          openDelete={openDelete}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Card body className="card--xs info-card card-with-border">
                      <Card.Text className="info-text info-text-md">
                        At this moment, there are no saved cards for this user.
                        Cards will be automatically stored here after completing
                        a successful transaction.
                      </Card.Text>
                    </Card>
                  )}
                </Stack>
              )}
            </>
          ) : (
            <>
              {(clientSecret || initialClientSecret) && (
                <Row className="m-0">
                  <Card body className="card--xs card-with-border">
                    <h1 className="normal normal-bold mb-3">
                      Add card information
                    </h1>
                    <CardForm
                      client_secret={clientSecret}
                      initialClientSecret={initialClientSecret}
                      setCard={setCard}
                      setStripe={setStripe}
                      setIsCardValid={setIsCardValid}
                      selectedCard={selectedCard}
                      setSelectedCard={setSelectedCard}
                    />
                  </Card>
                </Row>
              )}
            </>
          )}
        </>
      ) : (
        paymentMethod === 'CARD' &&
        (clientSecret || initialClientSecret) && (
          <Row className="m-0">
            <Card body className="card--xs card-with-border">
              <h1 className="normal normal-bold mb-3">Add card information</h1>
              <CardForm
                client_secret={clientSecret}
                initialClientSecret={initialClientSecret}
                setCard={setCard}
                setStripe={setStripe}
                setIsCardValid={setIsCardValid}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
              />
            </Card>
          </Row>
        )
      )}
      {paymentMethod === 'INVOICE' && (
        <SelectPaymentDueDate
          dueDateOpt={dueDateOpt}
          setDueDateOpt={setDueDateOpt}
          customDueDate={customDueDate}
          setCustomDueDate={setCustomDueDate}
        />
      )}
      {paymentMethod && (
        <>
          {/* invoice */}
          {paymentMethod === 'INVOICE' ? (
            <>
              {assignTo?.email && (
                <InfoCard
                  paymentPlans={paymentPlans}
                  installmentMonths={installmentMonths}
                  total={
                    showDonationInSummary
                      ? totalAmount + donationChargeAmount
                      : totalAmount
                  }
                  pricingObject={pricingObject}
                  price={price}
                  paymentMethod={paymentMethod}
                  customFeeTotal={totalCustomFeeAmount}
                  customFeeLineItems={customFeeSummaryLines}
                  showZeroServiceFee={showZeroServiceFee}
                  isPackage={isPackage}
                  requiresDonation={showDonationInSummary}
                  donationAmount={resolvedDonationAmount}
                  donationLineItems={donationLineItems}
                  donationLabel={
                    activeDonationRule?.name
                      ? `${activeDonationRule.name} Donation`
                      : 'Donation'
                  }
                >
                  <span>
                    A Stripe payment link will be sent to{' '}
                    <span className="text-primary">{assignTo?.email}</span>
                  </span>
                </InfoCard>
              )}
            </>
          ) : (
            <>
              {paymentMethod !== 'COMP' && (
                <>
                  {hasPricePerTicketProvided &&
                    (Number.isFinite(Number(amountToPay)) ||
                    donationChargeAmount > 0 ? (
                      <InfoCard
                        paymentPlans={paymentPlans}
                        installmentMonths={installmentMonths}
                        total={
                          showDonationInSummary
                            ? totalAmount + donationChargeAmount
                            : totalAmount
                        }
                        pricingObject={pricingObject}
                        price={price}
                        paymentMethod={paymentMethod}
                        customFeeTotal={totalCustomFeeAmount}
                        customFeeLineItems={customFeeSummaryLines}
                        showZeroServiceFee={showZeroServiceFee}
                        isPackage={isPackage}
                        requiresDonation={showDonationInSummary}
                        donationAmount={resolvedDonationAmount}
                        donationLineItems={donationLineItems}
                        donationLabel={
                          activeDonationRule?.name
                            ? `${activeDonationRule.name} Donation`
                            : 'Donation'
                        }
                      >
                        <span>
                          Amount to{' '}
                          {getAssignmentPaymentAmountDescription(paymentMethod)}
                          {paymentSchedule === 'payment-plan' && ' now'}:{' '}
                          <span className="normal normal-bold">
                            {formatCurrency(
                              paymentSchedule === 'payment-plan'
                                ? Number(paymentPlans[1]?.amount || 0) +
                                    cardDonationAmount
                                : amountToPay
                            )}
                          </span>
                        </span>
                      </InfoCard>
                    ) : (
                      <Card body className="card-xs card-with-border">
                        <LoadingContainer variant="dark">
                          <span className="d-block mt-2 small small-bold">
                            The price and fees are currently under calculation.
                          </span>
                        </LoadingContainer>
                      </Card>
                    ))}
                </>
              )}
            </>
          )}
          {paymentMethod === 'COMP' && compLabels && compLabels.length > 0 && (
            <Form.Group controlId="comp-label">
              <Form.Label>Comp type</Form.Label>
              <Form.Select
                name="comp-label"
                onChange={(e) => setSelectedCompLabel(e.target.value)}
                value={selectedCompLabel || ''}
                required
                aria-labelledby="comp-label"
              >
                <option disabled hidden value="">
                  Select Comp type
                </option>
                {compLabels?.map((label, idx) => (
                  <option key={idx} value={label?.id}>
                    {label?.attributes?.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {paymentMethod !== 'COMP' && buyerTypes && buyerTypes.length > 0 && (
            <Form.Group controlId="buyer-label">
              <Form.Label>Buyer type</Form.Label>
              <Form.Select
                name="buyer-label"
                onChange={(e) => setSelectedBuyerType(e.target.value)}
                value={selectedBuyerType || ''}
                required
                aria-labelledby="buyer-label"
              >
                <option disabled hidden value="">
                  Select Buyer type
                </option>
                {buyerTypes?.map((label, idx) => (
                  <option key={idx} value={label?.id}>
                    {label?.attributes?.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </>
      )}
    </Stack>
  )
}
